import Prism from 'prismjs';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-clojure';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-dart';
import 'prismjs/components/prism-docker';
import 'prismjs/components/prism-elixir';
import 'prismjs/components/prism-elm';
import 'prismjs/components/prism-erlang';
import 'prismjs/components/prism-haskell';
import 'prismjs/components/prism-scss';
import 'prismjs/components/prism-diff';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-makefile';
import 'prismjs/components/prism-graphql';
import 'prismjs/components/prism-kotlin';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-lua';
import 'prismjs/components/prism-matlab';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-nix';
import 'prismjs/components/prism-objectivec';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-fortran';
import 'prismjs/components/prism-promql';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-powershell';
import 'prismjs/components/prism-r';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-rust';
import 'prismjs/components/prism-scala';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-mermaid';
import 'prismjs/components/prism-swift';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-visual-basic';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-latex';
import 'prismjs/components/prism-pascal';

Prism.manual = true;

type Language = {
  key: string;
  label: string;
  aliases?: string[];
};

export const PlainLanguage = {
  key: 'plain',
  label: 'Plain',
} as const satisfies Language;

export const languageList: Language[] = [
  PlainLanguage,
  { key: 'bash', label: 'Bash', aliases: ['sh', 'shell', 'zsh'] },
  { key: 'cs', label: 'C#', aliases: ['c#'] },
  { key: 'cpp', label: 'C++', aliases: ['c', 'cc', 'c++'] },
  {
    key: 'clojure',
    label: 'Clojure',
    aliases: ['clj', 'cl2', 'cljc', 'cljs'],
  },
  { key: 'scss', label: 'CSS/SCSS', aliases: ['css'] },
  { key: 'dart', label: 'Dart' },
  { key: 'diff', label: 'Diff', aliases: ['patch'] },
  {
    key: 'docker',
    label: 'Dockerfile',
    aliases: ['dockerfile'],
  },
  { key: 'elixir', label: 'Elixir', aliases: ['ex', 'exs'] },
  { key: 'elm', label: 'Elm' },
  { key: 'erlang', label: 'Erlang', aliases: ['erl', 'es'] },
  { key: 'fortran', label: 'Fortran' },
  { key: 'go', label: 'Go', aliases: ['golang'] },
  { key: 'graphql', label: 'GraphQL', aliases: ['gql'] },
  {
    key: 'haskell',
    label: 'Haskell',
    aliases: ['hs', 'hsc'],
  },
  { key: 'xml', label: 'HTML/XML', aliases: ['htm', 'html', 'xhtml'] },
  { key: 'java', label: 'Java' },
  {
    key: 'javascript',
    label: 'JavaScript',
    aliases: ['js', 'jsx', 'node', 'nodejs'],
  },
  { key: 'json', label: 'JSON' },
  {
    key: 'kotlin',
    label: 'Kotlin',
    aliases: ['kt', 'ktm', 'kts'],
  },
  { key: 'lua', label: 'Lua' },
  { key: 'makefile', label: 'Makefile' },
  { key: 'markdown', label: 'Markdown' },
  { key: 'matlab', label: 'MATLAB' },
  { key: 'mermaid', label: 'Mermaid' },
  { key: 'nix', label: 'Nix' },
  {
    key: 'objectivec',
    label: 'Objective-C',
    aliases: ['obj-c', 'objc'],
  },
  { key: 'pascal', label: 'Pascal' },
  { key: 'php', label: 'PHP' },
  { key: 'powershell', label: 'PowerShell' },
  { key: 'promql', label: 'PromQL', aliases: ['prometheus'] },
  { key: 'python', label: 'Python', aliases: ['py'] },
  { key: 'r', label: 'R' },
  {
    key: 'ruby',
    label: 'Ruby',
    aliases: ['jruby', 'rake', 'rb'],
  },
  { key: 'rust', label: 'Rust', aliases: ['rs'] },
  { key: 'scala', label: 'Scala' },
  { key: 'sql', label: 'SQL', aliases: ['psql'] },
  { key: 'swift', label: 'Swift' },
  { key: 'latex', label: 'Tex/KaTeX', aliases: ['katex'] },
  {
    key: 'typescript',
    label: 'TypeScript',
    aliases: ['ts', 'tsx'],
  },
  {
    key: 'visual-basic',
    label: 'Visual Basic',
    aliases: ['vb', 'visualbasic'],
  },
  { key: 'yaml', label: 'YAML', aliases: ['yml'] },
];

export const identifyLanguage = (language: string): string | undefined => {
  const lowercased = language.toLowerCase();
  return languageList.find(
    ({ key, aliases }) => key === lowercased || aliases?.includes(lowercased),
  )?.key;
};

export function getGrammar(language: string | null) {
  switch (language) {
    case 'javascript':
      return Prism.languages.jsx;
    case 'typescript':
      return Prism.languages.tsx;
    case 'xml':
      return Prism.languages.markup;
    default:
      return language ? Prism.languages[language] : undefined;
  }
}

export default Prism;
